import { ChangeEvent } from "react"

import TextField, { TextFieldProps } from "@material-ui/core/TextField"
import { css, cx } from "@emotion/css"

import CloseIcon from "@material-ui/icons/Close"
import IconButton from "@material-ui/core/IconButton"
import InputAdornment from "@material-ui/core/InputAdornment"
import SearchIcon from "@material-ui/icons/Search"
import { useI18n } from "providers/I18n"

const styles = {
  container: css`
    width: 176px;
  `,
  root: css`
    .focusedPadding {
      padding-left: 0;
    }
    :focus-within {
      .focusedPadding {
        padding-left: var(--spacing-s);
      }
    }
  `,
  dense: css`
    padding-top: 8.5px !important;
    padding-bottom: 7.5px !important;
    padding-left: 0;
    padding-right: 0;
    margin-left: -6px;
  `,
  icon: css`
    font-size: 1.5rem;
    color: var(--dark-grey);
  `,
  clear: css`
    width: var(--spacing-m);
    height: var(--spacing-m);
  `,
  startAdornment: css`
    padding-left: var(--spacing-xs);
  `,
  onHoverAndFocus: css`
    .closeIcon {
      opacity: 0;
    }
    .searchIcon {
      display: none;
    }
    :focus-within {
      .closeIcon {
        opacity: 1;
      }
    }
    &:hover {
      .closeIcon {
        opacity: 1;
      }
    }
    :not(:focus-within) {
      .searchIcon {
        display: flex;
      }
    }
  `,
}

export type TextInputProps = Omit<
  TextFieldProps,
  "name" | "value" | "onChange"
> & {
  name: string
  value?: string
  setValue: (name: string, value: any) => void
}

export function Search(props: TextInputProps) {
  const { name, value, setValue, ...rest } = props
  const { t } = useI18n()

  const handleChange = (
    evt: ChangeEvent<{
      name?: string | undefined
      value: unknown
    }>,
  ) => {
    setValue(name, evt.target.value as string)
  }

  const handleClear = () => {
    setValue(name, "")
  }

  return (
    <div id="table_search_field" className={styles.container}>
      <TextField
        {...rest}
        fullWidth={true}
        placeholder={t("inputs.search.placeholder")}
        variant="outlined"
        margin="none"
        size="small"
        id={name}
        value={value}
        onChange={handleChange}
        classes={{
          root: styles.onHoverAndFocus,
        }}
        InputProps={{
          classes: {
            root: styles.root,
            inputMarginDense: cx(styles.dense, "focusedPadding"),
            input: "BodyBody-2BlackRegular",
            adornedStart: styles.startAdornment,
          },
          inputProps: {
            autoComplete: "new-password",
            form: {
              autoComplete: "off",
            },
          },
          startAdornment: (
            <InputAdornment position="start" className="searchIcon">
              <SearchIcon
                className={styles.icon}
                style={{ display: value === "" ? "flex" : "none" }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" className="closeIcon">
              <IconButton
                onClick={handleClear}
                aria-label="clear"
                size="small"
                edge="end"
                type="reset"
                style={{ opacity: value === "" ? 0 : 1 }}
              >
                <CloseIcon className={styles.clear} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  )
}
